import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  IconButton,
  Fade,
  Alert,
  Snackbar,
  InputAdornment,
} from "@mui/material";
import CommonLoader from "../common/CommonLoader";
import { GetInventoryReorderNotice } from "../../services/InventoryTrackerService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CachedIcon from "@mui/icons-material/Cached";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import { GetVLPDashboardData } from "../../services/DashboardService";
import AutocompleteSelectUser from "../common/AutocompleteSelectUser";
import UserService from "../../../globel-constants/UserService";
import OrdersPanel from "./sections/OrdersPanel";
import InvoicesPanel from "./sections/InvoicesPanel";
import QuotesPanel from "./sections/QuotesPanel"
import SupplyOrdersPanel from "./sections/SupplyOrdersPanel";
import BottleOrdersPanel from "./sections/BottleOrdersPanel";
import EtkQueuePanel from "./sections/EtkQueuePanel";
import UserSelectorDialog from "../common/UserSelectorDialog"; // Add this import
import AnalystPanel from "./sections/AnalystPanel";

import "./VLPDashboardRedesign.css";

const sumCounts = (arr) =>
  Array.isArray(arr) ? arr.reduce((a, b) => a + (b?.count || 0), 0) : 0;
const sumAmounts = (arr) =>
  Array.isArray(arr) ? arr.reduce((a, b) => a + (b?.count || 0), 0) : 0;

export default function VLPDashboardRedesign() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserObject, setSelectedUserObject] = useState(null); // Add this state
  const [userSelectorOpen, setUserSelectorOpen] = useState(false); // Add this state
  const [loggedInUser, setLoggedInUser] = useState();
  // ENB-584: products to order now / next week, shown to Warehouse Admins only.
  const [reorderNotice, setReorderNotice] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({
    orders: false,
    invoices: false,
    supplyOrders: false,
    bottles: false,
    etkQueue: false,
    analyst: false,
  });

  // Flag to track if initial load is done
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Add user select handler
  const handleUserSelect = useCallback((user) => {
    if (user) {
      setSelectedUserObject(user);
      setSelectedUser(user.operator || "");
    } else {
      setSelectedUserObject(null);
      setSelectedUser("");
    }
    setUserSelectorOpen(false);
  }, []);

  // Load filters on mount
  useEffect(() => {
    const user = UserService.getUserInfoFromLocalStorage?.();
    setLoggedInUser(user);

    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split("T")[0];
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];

    setStartDate(start);
    setEndDate(end);
    setSelectedUser(user?.operator || "");

    if (user?.permissions?.isWarehouseAdmin || user?.isWarehouseAdmin) {
      GetInventoryReorderNotice()
        .then((response) => setReorderNotice(response?.results || []))
        .catch((err) => console.error("Error loading reorder notice:", err));
    }
  }, []);

  // Direct API call function (removed dependencies to prevent auto-calls)
  const loadSummary = useCallback(async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    // Validate date range
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await GetVLPDashboardData(startDate, endDate, selectedUser);
      setSummary(res);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error loading summary:", err);
      setError("Failed to load dashboard data. Please try again.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []); // Remove startDate, endDate, selectedUser dependencies

  // Create a separate function that captures current values
  const executeLoadSummary = useCallback(async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    // Validate date range
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await GetVLPDashboardData(startDate, endDate, selectedUser);
      setSummary(res);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error loading summary:", err);
      setError("Failed to load dashboard data. Please try again.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedUser]);

  // Initial load ONLY - runs once when startDate is set (first load only)
  useEffect(() => {
    if (startDate && endDate && !initialLoadDone) {
      executeLoadSummary();
      setInitialLoadDone(true);
    }
  }, [startDate, endDate, executeLoadSummary, initialLoadDone]);

  const handleKpiClick = useCallback((route) => {
    if (route) {
      window.open(route, "_blank");
    }
  }, []);

  // Optimized KPI calculation
  // Amount cards come first and span a row of three (lg: 4); count cards follow four per row.
  const kpis = useMemo(() => {
    if (!summary) return [];
    const supplyOrderCount = sumCounts(summary?.supplyOrderStatuses);
    return [
      {
        label: "Quote Amount",
        value: `$${sumAmounts(summary?.quoteAmounts).toLocaleString()}`,
        hint: "Total (all statuses)",
        color: "success",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "💰",
        trend: "+15%",
        lg: 4,
        // No route - not clickable
      },
      {
        label: "Invoice Amount",
        value: `$${sumAmounts(summary?.invoiceAmounts).toLocaleString()}`,
        hint: "Open + Invoiced + Deposited",
        color: "success",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "💰",
        trend: "+15%",
        lg: 4,
        // No route - not clickable
      },
      {
        label: "Supply Order Amount",
        value: `$${sumAmounts(summary?.supplyOrderAmounts).toLocaleString()}`,
        hint: `${supplyOrderCount.toLocaleString()} POs (all statuses)`,
        color: "success",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "🚚",
        lg: 4,
        route: "/vlp/inventory-supply-orders",
        clickable: true,
      },
      {
        label: "Orders",
        value: sumCounts(summary?.orderStatuses),
        hint: "Total (all statuses)",
        color: "primary",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "📋",
        trend: "+12%",
        route: "/vlp/lab-orders", // ✅ Added route
        clickable: true, // ✅ Added clickable flag
      },
      {
        label: "Invoices",
        value: sumCounts(summary?.invoiceStatuses),
        hint: "Total (all statuses)",
        color: "info",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "🧾",
        trend: "+8%",
        route: "/vlp/invoices", // ✅ Added route
        clickable: true, // ✅ Added clickable flag
      },
      {
        label: "Bottle Orders",
        value: sumCounts(summary?.bottleOrderStatuses),
        hint: "Total bottles",
        color: "warning",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "🍶",
        trend: "+5%",
        route: "/vlp/bottle-orders", // ✅ Added route
        clickable: true, // ✅ Added clickable flag
      },
      {
        label: "Analyst Pending",
        value: sumCounts(summary?.analystResultStatuses),
        hint: "Pending results",
        color: "error",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "🔬",
        trend: "-3%",
        // No route - not clickable
      },
      {
        label: "ETK Orders",
        value: sumCounts(summary?.etkOrderStatuses),
        hint: "ETK clients",
        color: "secondary",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "🔄",
        trend: "+22%",
        route: "/vlp/etk-registration-queue", // ✅ Added route
        clickable: true, // ✅ Added clickable flag
      },
      {
        label: "Data Team Orders",
        value: sumCounts(summary?.dataTeamOrderStatusCounts),
        hint: "Reviewed period",
        color: "default",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "📊",
        trend: "+7%",
        // No route - not clickable
      },
      {
        label: "Samples",
        value: sumCounts(summary?.sampleCounts),
        hint: "Received in period",
        color: "info",
        gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
        icon: "🧪",
        // No route - not clickable
      },
    ];
  }, [summary]);

  const handleAccordionChange = useCallback(
    (panel) => (_, isExpanded) => {
      setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
    },
    []
  );

  // Updated to use executeLoadSummary
  const handleForceRefresh = useCallback(() => {
    if (!loading) {
      executeLoadSummary();
    }
  }, [executeLoadSummary, loading]);

  // Updated search handler
  const handleSearch = useCallback(() => {
    executeLoadSummary();
  }, [executeLoadSummary]);

  return (
    <Box className="vlp-dashboard-root">
      {/* Enhanced Header with status indicators */}
      <Box className="dashboard-header">
        <Box className="header-content">
          <Box className="header-icon-wrapper">
            <DashboardIcon className="header-icon" />
          </Box>
          <Box>
            <Typography variant="h4" className="header-title">
              Extend's Dashboard
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" className="header-subtitle">
                Monitor operations and drill down when needed
              </Typography>
              {lastUpdated && (
                <Chip
                  label={`Updated: ${lastUpdated.toLocaleTimeString()}`}
                  size="small"
                  color={"success"}
                  icon={<RefreshIcon />}
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
        {summary?.securityGroup && (
          <Chip
            label={loading ? "Refreshing..." : "Get fresh data"}
            className="security-chip"
            title="Force refresh data"
            onClick={handleForceRefresh}
            icon={loading ? <CachedIcon className="spin" /> : <RefreshIcon />}
            disabled={loading}
          />
        )}
      </Box>

      {reorderNotice.length > 0 && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.open("/vlp/inventory-reorder", "_blank")}>
              Reorder Report
            </Button>
          }
        >
          <strong>
            Inventory reorder: {reorderNotice.filter((r) => r.reorderStatus === "Order Now").length} to order now,{" "}
            {reorderNotice.filter((r) => r.reorderStatus === "Order Next Week").length} next week.
          </strong>{" "}
          {reorderNotice
            .slice(0, 5)
            .map((r) => r.productName)
            .join(", ")}
          {reorderNotice.length > 5 ? ` and ${reorderNotice.length - 5} more` : ""}
        </Alert>
      )}

      {/* Enhanced Filters */}
      <Card className="dashboard-filters">
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="From Date"
                type="date"
                fullWidth
                size="small"
                variant="outlined"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                className="modern-input"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="To Date"
                type="date"
                fullWidth
                size="small"
                variant="outlined"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                className="modern-input"
              />
            </Grid>

            {loggedInUser?.groups?.includes("admin") && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="User"
                  value={
                    selectedUserObject
                      ? `${selectedUserObject.operator.trim()} - ${selectedUserObject.aliasName.trim()}`
                      : "Click to select user"
                  }
                  size="small"
                  fullWidth
                  onClick={() => setUserSelectorOpen(true)}
                  InputProps={{
                    readOnly: true,
                    style: { cursor: "pointer" },
                    startAdornment: !selectedUserObject && (
                      <InputAdornment position="start">
                        <PersonIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: selectedUserObject && (
                      <InputAdornment position="end">
                        <EditIcon color="primary" sx={{ fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                startIcon={
                  loading ? <CircularProgress size={16} /> : <FilterAltIcon />
                }
                onClick={handleSearch}
                className="search-button"
                disabled={loading || !startDate || !endDate}
              >
                {loading ? "Searching..." : "Search"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced KPIs with performance metrics */}
      <Fade in={!loading} timeout={600}>
        <Box>
          {loading ? (
            <Box className="loading-container">
              <CircularProgress size={48} thickness={4} />
              <Typography variant="h6" className="loading-text">
                Loading dashboard insights...
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3} className="kpi-grid">
              {kpis.map((kpi, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={kpi.lg || 3}
                  key={kpi.label}
                >
                  <Fade in timeout={800 + index * 100}>
                    <Card
                      className={`kpi-card modern-kpi-${kpi.color}`}
                      onClick={
                        kpi.clickable
                          ? () => handleKpiClick(kpi.route)
                          : undefined
                      }
                      sx={{
                        cursor: kpi.clickable ? "pointer" : "default",
                        transition: "all 0.2s ease-in-out",
                        ...(kpi.clickable && {
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px rgba(0, 125, 209, 0.15)",
                          },
                          "&:active": {
                            transform: "translateY(0px)",
                          },
                        }),
                      }}
                    >
                      <CardContent className="kpi-content">
                        <Box className="kpi-header">
                          <Typography className="kpi-icon">
                            {kpi.icon}
                          </Typography>
                          <Box>
                            <Typography variant="caption" className="kpi-title">
                              {kpi.label}
                              {/* {kpi.clickable && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    marginLeft: 1,
                                    fontSize: "0.7rem",
                                    opacity: 0.7,
                                    fontStyle: "italic",
                                  }}
                                >
                                  (click to view)
                                </Typography>
                              )} */}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="h3" className="kpi-value">
                          {kpi.value}
                        </Typography>
                        <Typography variant="body2" className="kpi-hint">
                          {kpi.hint}
                        </Typography>
                        <Box
                          className="kpi-gradient-bar"
                          style={{ background: kpi.gradient }}
                        />
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Fade>

      {/* Enhanced Detail Sections with lazy loading */}
      <Box className="detail-sections">
        {[
          {
            key: "orders",
            title: "Orders",
            component: OrdersPanel,
            props: { startDate, endDate, selectedUser },
          },
          {
            key: "invoices",
            title: "Invoices",
            component: InvoicesPanel,
            props: { startDate, endDate },
          },
          {
            key: "quotes",
            title: "Quotes",
            component: QuotesPanel,
            props: { startDate, endDate },
          },
          {
            key: "supplyOrders",
            title: "Supply Orders",
            component: SupplyOrdersPanel,
            props: { startDate, endDate },
          },
          {
            key: "bottles",
            title: "Bottle Orders",
            component: BottleOrdersPanel,
            props: { startDate, endDate },
          },
          {
            key: "etkQueue",
            title: "ETK Queue",
            component: EtkQueuePanel,
            props: {},
          },
          {
            key: "analyst", // ✅ added
            title: "Analyst Pending",
            component: AnalystPanel,
            props: { startDate, endDate, selectedUser },
          },
        ].map(({ key, title, component: Component, props }) => (
          <Accordion
            key={key}
            expanded={expanded[key]}
            onChange={handleAccordionChange(key)}
            className="modern-accordion"
            TransitionProps={{ timeout: 400 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className="accordion-summary"
            >
              <Typography variant="h6" className="accordion-title">
                {title}
              </Typography>
              {summary && expanded[key] && (
                <Chip
                  label="Live Data"
                  size="small"
                  color="primary"
                  variant="outlined"
                  style={{ marginLeft: "auto", marginRight: 16 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              {expanded[key] && <Component {...props} />}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      <CommonLoader loading={loading} />
      {/* Error handling */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* User Selector Dialog */}
      <UserSelectorDialog
        open={userSelectorOpen}
        onClose={() => setUserSelectorOpen(false)}
        onSelect={handleUserSelect}
        multiSelect={false}
        filter=""
      />
    </Box>
  );
}
